import { safari, playwright } from './platform.js';
import { HAS_WINDOW } from './window.js';

/** Whether or not this browser supports the pointerLock api. */
const HAS_POINTER_LOCK = HAS_WINDOW && !!document.documentElement.requestPointerLock;
/**
 * Whether or not pointerLock should be used on this browser. Some browsers, like Safari,
 * support pointer lock. But the experience is really bad, so we don't use it anyway.
 */
const USE_POINTER_LOCK = HAS_POINTER_LOCK && !safari && !playwright;
/** Calls `document.exitPointerLock()` but only if pointer lock is active. */
function exitPointerLock() {
    if (document.pointerLockElement) {
        document.exitPointerLock();
    }
}
/** Returns whether the element has pointer lock, including from within a shadow root. */
function hasPointerLock(el) {
    const root = el.getRootNode();
    // A disconnected element may be its own root. This capability check both narrows Node without a
    // cross-realm instanceof check and rejects roots that cannot own pointer lock. A ShadowRoot then
    // exposes the locked descendant instead of the host returned by its surrounding Document.
    if (!('pointerLockElement' in root))
        return false;
    return root.pointerLockElement === el;
}
/**
 * Requests pointer lock on the given element. Gracefully handles the different ways browsers
 * handle pointer lock, returning a rejected promise if the pointer lock request fails or was
 * denied due to insufficient permissions.
 */
async function requestPointerLock(el) {
    // This can fail if it is invoked from a sandboxed iframe, and also during our playwright tests.
    // In those cases, the promise will be rejected, which should be handled by the caller.
    // Older browsers don't return anything here, but newer ones return a promise. Typescript
    // types have not caught up.
    const result = el.requestPointerLock();
    // If the browser doesn't return a promise, then it isn't behind
    // a permission and we can assume it was successful.
    if (result) {
        // There's a new permissions api in chrome which, when denied, causes calls to
        // `requestPointerLock` to return a rejected promise. To avoid any console errors,
        // the caller should handle rejected promises from calling this function.
        //
        // https://developer.chrome.com/blog/keyboard-lock-pointer-lock-permission
        await result;
    }
}

export { HAS_POINTER_LOCK, USE_POINTER_LOCK, exitPointerLock, hasPointerLock, requestPointerLock };
//# sourceMappingURL=pointer-lock.js.map
